import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { DisciplineSchema } from "../common/tunisia.js";
import { PhaseTypeSchema } from "../projects/index.js";
import { DocumentMetadataSchema, RevisionReasonSchema } from "./metadata.js";
import { DocumentTypeSchema } from "./naming.js";
import { DocumentVersionStatusSchema, UploadRejectionReasonSchema } from "./uploads.js";
const DOCUMENT_PERMISSIONS = [
  "can_view",
  "can_comment",
  "can_edit",
  "can_request_approval",
  "can_share",
  "can_delete"
];
const DocumentPermissionSchema = z.enum(DOCUMENT_PERMISSIONS);
const DocumentSummarySchema = z.strictObject({
  id: IdSchema,
  spaceId: IdSchema,
  folderId: IdSchema,
  /** Null until the pattern's metadata are complete. */
  code: z.string().nullable(),
  title: z.string(),
  discipline: DisciplineSchema.nullable(),
  phaseType: PhaseTypeSchema.nullable(),
  docType: DocumentTypeSchema.nullable(),
  zone: z.string().nullable(),
  scale: z.string().nullable(),
  /** The current (latest available) version; null while the first one is checked or refused. */
  currentVersionId: IdSchema.nullable(),
  revisionIndex: z.string().nullable(),
  sizeBytes: z.int().nullable(),
  mimeType: z.string().nullable(),
  /** Status of the latest version (a new one may be `scanning` above an available one). */
  latestVersionStatus: DocumentVersionStatusSchema.nullable(),
  ownerOrgId: IdSchema,
  /** Null when the organisation is not visible to the caller (a partner, until COL). */
  ownerOrgName: z.string().nullable(),
  createdBy: IdSchema,
  /** The approval flow holding the document (no new version until it ends, GED-BE-06). */
  lockedByFlowId: IdSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  version: z.int().min(1),
  _permissions: z.array(DocumentPermissionSchema)
});
const DocumentDetailSchema = DocumentSummarySchema.extend({
  /** Metadata the organisation's pattern still needs for a code. */
  missingMetadata: z.array(z.enum(["discipline", "phaseType", "docType", "zone"])),
  /** Deliverables of the project linked to this document. */
  deliverables: z.array(z.strictObject({ id: IdSchema, title: z.string() }))
});
const DOCUMENT_SORTS = ["code", "title", "updatedAt"];
const DocumentListQuerySchema = z.strictObject({
  folderId: IdSchema,
  discipline: DisciplineSchema.optional(),
  phaseType: PhaseTypeSchema.optional(),
  docType: DocumentTypeSchema.optional(),
  /** Part of the code or the title (full-text search arrives with GED-BE-08). */
  q: z.string().trim().min(1).max(100).optional(),
  sort: z.enum(DOCUMENT_SORTS).default("code"),
  order: z.enum(["asc", "desc"]).default("asc"),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  cursor: z.string().min(1).max(512).optional()
});
const DocumentListSchema = z.strictObject({
  items: z.array(DocumentSummarySchema),
  nextCursor: z.string().nullable()
});
const DocumentParamsSchema = z.strictObject({ spaceId: IdSchema, documentId: IdSchema });
const UpdateDocumentRequestSchema = DocumentMetadataSchema.extend({ folderId: IdSchema }).partial().refine((p) => Object.keys(p).length > 0, { message: "nothing to update" });
const DocumentVersionSchema = z.strictObject({
  id: IdSchema,
  documentId: IdSchema,
  revisionIndex: z.string().nullable(),
  revisionReason: z.string().nullable(),
  code: z.string().nullable(),
  originalFilename: z.string(),
  sizeBytes: z.int(),
  mimeType: z.string().nullable(),
  sha256: z.string().nullable(),
  status: DocumentVersionStatusSchema,
  rejectionReason: UploadRejectionReasonSchema.nullable(),
  uploadedBy: IdSchema,
  uploadedByName: z.string().nullable(),
  ownerOrgId: IdSchema,
  ownerOrgName: z.string().nullable(),
  restoredFromVersionId: IdSchema.nullable(),
  current: z.boolean(),
  createdAt: IsoDateTimeSchema,
  availableAt: IsoDateTimeSchema.nullable()
});
const DocumentVersionListSchema = z.strictObject({ items: z.array(DocumentVersionSchema) });
const VersionParamsSchema = z.strictObject({
  spaceId: IdSchema,
  documentId: IdSchema,
  versionId: IdSchema
});
const RestoreVersionRequestSchema = z.strictObject({ reason: RevisionReasonSchema });
export {
  DOCUMENT_PERMISSIONS,
  DOCUMENT_SORTS,
  DocumentDetailSchema,
  DocumentListQuerySchema,
  DocumentListSchema,
  DocumentParamsSchema,
  DocumentPermissionSchema,
  DocumentSummarySchema,
  DocumentVersionListSchema,
  DocumentVersionSchema,
  RestoreVersionRequestSchema,
  UpdateDocumentRequestSchema,
  VersionParamsSchema
};
//# sourceMappingURL=documents.js.map